import * as Sentry from "@sentry/svelte";
import type { FilterType, SpaceKind } from "@workadventure/messages";
import type { Subscription } from "rxjs";
import { z } from "zod";
import { MapStore } from "@workadventure/store-utils";
import type { Readable } from "svelte/store";
import { derived, get } from "svelte/store";
import type { FloorSpeaker, RaisedHand, SpaceInterface } from "../SpaceInterface";
import { SpaceAlreadyExistError, SpaceDoesNotExistError } from "../Errors/SpaceError";
import type { VideoBox } from "../VideoBox";
import { Space } from "../Space";
import type { SpaceStateManager } from "../SpaceStateManager";
import type { RoomConnection } from "../../Connection/RoomConnection";
import { connectionManager } from "../../Connection/ConnectionManager";
import type { SpaceRegistryInterface } from "./SpaceRegistryInterface";
/**
 * The subset of properties of RoomConnection that are used by the SpaceRegistry / Space / SpaceFilter class.
 * This interface has a single purpose: making the creation of test doubles easier in unit tests.
 */
export type RoomConnectionForSpacesInterface = Pick<
    RoomConnection,
    | "closed"
    | "initSpaceUsersMessageStream"
    | "addSpaceUserMessageStream"
    | "updateSpaceUserMessageStream"
    | "removeSpaceUserMessageStream"
    | "updateSpaceMetadataMessageStream"
    | "spacePublicMessageEvent"
    | "spacePrivateMessageEvent"
    | "emitPrivateSpaceEvent"
    | "emitPublicSpaceEvent"
    | "emitRemoveSpaceFilter"
    | "emitAddSpaceFilter"
    | "emitLeaveSpace"
    | "emitJoinSpace"
    | "alterSpaceState"
    | "spaceStatePatchMessageStream"
    | "emitUpdateSpaceMetadata"
    | "emitUpdateSpaceUserMessage"
    | "spaceDestroyedMessage"
    | "emitBackEvent"
    | "emitVideoQualityReport"
>;

/** Flattens per-space lists keyed by spaceUserId, keeping the first entry of a user present in several spaces. */
export function uniqueBySpaceUserId<T extends { spaceUserId: string }>(lists: T[][]): T[] {
    const seen = new Set<string>();
    return lists.flat().filter((entry) => {
        if (seen.has(entry.spaceUserId)) {
            return false;
        }
        seen.add(entry.spaceUserId);
        return true;
    });
}

/**
 * This class is in charge of creating, joining, leaving and deleting Spaces.
 * It acts both as a factory and a registry.
 */
export class SpaceRegistry implements SpaceRegistryInterface {
    private spaces: MapStore<string, Space> = new MapStore<string, Space>();
    public readonly spacesEligibleForRecording: Readable<Space[]>;
    // Spaces the local user currently syncs their media (and raised hand) with: the candidates for raising a hand.
    public readonly spacesSynchronizingMedia: Readable<Space[]>;
    private leavingSpacesPromises: Map<string, Promise<void>> = new Map<string, Promise<void>>();
    private joiningSpacesPromises: Map<string, Promise<Space>> = new Map<string, Promise<Space>>();
    private initSpaceUsersMessageStreamSubscription: Subscription;
    private addSpaceUserMessageStreamSubscription: Subscription;
    private updateSpaceUserMessageStreamSubscription: Subscription;
    private removeSpaceUserMessageStreamSubscription: Subscription;
    private updateSpaceMetadataMessageStreamSubscription: Subscription;
    private spaceStatePatchMessageStreamSubscription: Subscription;
    private proximityPublicMessageEventSubscription: Subscription;
    private proximityPrivateMessageEventSubscription: Subscription;
    private spaceDestroyedMessageSubscription: Subscription;

    public readonly videoStreamStore: Readable<Map<string, VideoBox>> = derived(this.spaces, ($spaces, set) => {
        if ($spaces.size === 0) {
            set(new Map());
            return () => {};
        }

        const spaceStores = Array.from($spaces.values()).map((space) => space.videoStreamStore);

        const combinedStore = derived(spaceStores, (allSpaceStreams) => {
            const aggregatedPeers = new Map<string, VideoBox>();

            allSpaceStreams.forEach((spaceStreams) => {
                spaceStreams.forEach((streamable, userId) => {
                    aggregatedPeers.set(userId, streamable);
                });
            });

            return aggregatedPeers;
        });

        const unsubscribe = combinedStore.subscribe((aggregatedPeers) => {
            set(new Map(aggregatedPeers));
        });

        return unsubscribe;
    });

    public readonly screenShareStreamStore: Readable<Map<string, VideoBox>> = derived(this.spaces, ($spaces, set) => {
        if ($spaces.size === 0) {
            set(new Map());
            return () => {};
        }

        const spaceStores = Array.from($spaces.values()).map((space) => space.screenShareStreamStore);

        const combinedStore = derived(spaceStores, (allSpaceStreams) => {
            const aggregatedPeers = new Map<string, VideoBox>();

            allSpaceStreams.forEach((spaceStreams) => {
                spaceStreams.forEach((streamable, userId) => {
                    aggregatedPeers.set(userId, streamable);
                });
            });

            return aggregatedPeers;
        });

        const unsubscribe = combinedStore.subscribe((aggregatedPeers) => {
            set(new Map(aggregatedPeers));
        });

        return unsubscribe;
    });

    public readonly isLiveStreamingStore: Readable<boolean> = derived(this.spaces, ($spaces, set) => {
        if ($spaces.size === 0) {
            set(false);
            return () => {};
        }

        const stores = Array.from($spaces.values(), (space) => space.isStreamingVideoStore);
        return derived(stores, (list) => list.some(Boolean)).subscribe(set);
    });

    // Aggregated raised-hands queue across all spaces. A user can raise their hand in several spaces at once (e.g. a
    // bubble and the listener zone it stands in), so the same spaceUserId can appear in several lists: keep the first.
    public readonly raisedHandsStore: Readable<RaisedHand[]> = derived(this.spaces, ($spaces, set) => {
        if ($spaces.size === 0) {
            set([]);
            return () => {};
        }

        const stores = Array.from($spaces.values(), (space) => space.state.raisedHandsStore);
        return derived(stores, uniqueBySpaceUserId).subscribe(set);
    });

    // Aggregated list of users currently holding the floor across all spaces (in practice only the meeting space).
    public readonly speakingUsersStore: Readable<FloorSpeaker[]> = derived(this.spaces, ($spaces, set) => {
        if ($spaces.size === 0) {
            set([]);
            return () => {};
        }

        const stores = Array.from($spaces.values(), (space) => space.state.speakingUsersStore);
        return derived(stores, uniqueBySpaceUserId).subscribe(set);
    });

    /**
     * Gives the floor to a user identified by their spaceUserId, resolving the space from the raised-hands queue.
     * Used by the host "raised hands" panel, which only has the spaceUserId (not the SpaceUserExtended) — in
     * particular for a megaphone speaker who does not receive listeners' SpaceUser.
     */
    public async giveFloor(spaceUserId: string): Promise<void> {
        await this.findStateWithEntry(spaceUserId, (state) => state.raisedHandsStore)?.giveFloor(spaceUserId);
    }

    /**
     * Takes the floor back from a user identified by their spaceUserId, resolving the space from the list of
     * current speakers. Counterpart of giveFloor for the host "raised hands" panel.
     */
    public async revokeFloor(spaceUserId: string): Promise<void> {
        await this.findStateWithEntry(spaceUserId, (state) => state.speakingUsersStore)?.revokeFloor(spaceUserId);
    }

    /** Lowers the hand of a user who raised it (moderation), resolving the space from the raised-hands queue. */
    public async lowerHand(spaceUserId: string): Promise<void> {
        await this.findStateWithEntry(spaceUserId, (state) => state.raisedHandsStore)?.lowerHand(spaceUserId);
    }

    /** The joined spaces for which `select` currently holds, kept up to date as spaces come and go. */
    private spacesWhere(select: (space: Space) => Readable<boolean>): Readable<Space[]> {
        return derived(this.spaces, ($spaces, set) => {
            const spaces = Array.from($spaces.values());

            if (spaces.length === 0) {
                set([]);
                return () => {};
            }

            return derived(spaces.map(select), (holds) => spaces.filter((_, index) => holds[index])).subscribe(set);
        });
    }

    private findStateWithEntry(
        spaceUserId: string,
        list: (state: SpaceStateManager) => Readable<{ spaceUserId: string }[]>,
    ): SpaceStateManager | undefined {
        return Array.from(this.spaces.values(), (space) => space.state).find((state) =>
            get(list(state)).some((entry) => entry.spaceUserId === spaceUserId),
        );
    }

    public readonly isLiveStreamingAudioStore: Readable<boolean> = derived(this.spaces, ($spaces, set) => {
        if ($spaces.size === 0) {
            set(false);
            return () => {};
        }

        const stores = Array.from($spaces.values(), (space) => space.isStreamingAudioStore);
        return derived(stores, (list) => list.some(Boolean)).subscribe(set);
    });

    public readonly shouldPublishScreenShareStore: Readable<boolean> = derived(this.spaces, ($spaces, set) => {
        if ($spaces.size === 0) {
            set(false);
            return () => {};
        }

        const stores = Array.from($spaces.values(), (space) => space.shouldPublishScreenShareStore);
        return derived(stores, (list) => list.some(Boolean)).subscribe(set);
    });

    constructor(
        private roomConnection: RoomConnectionForSpacesInterface,
        private connectStream = connectionManager.roomConnectionStream,
    ) {
        this.initSpaceUsersMessageStreamSubscription = roomConnection.initSpaceUsersMessageStream.subscribe(
            (message) => {
                if (!message.users) {
                    console.error(message);
                    throw new Error("initSpaceUsersMessage is missing users");
                }

                const space = this.spaces.get(message.spaceName);

                if (!space) {
                    console.error("Space does not exist", message.spaceName);
                    return;
                }

                space.initUsers(message.users);
                space.initMetadata(message.metadata);
            },
        );

        this.spacesEligibleForRecording = this.spacesWhere((space) => space.shouldDisplayRecordButton);
        this.spacesSynchronizingMedia = this.spacesWhere((space) => space.spacePeerManager.mediaSynchronizedStore);

        this.addSpaceUserMessageStreamSubscription = roomConnection.addSpaceUserMessageStream.subscribe((message) => {
            if (!message.user) {
                console.error(message);
                throw new Error("addSpaceUserMessage is missing a user");
            }

            this.spaces.get(message.spaceName)?.addUser(message.user);
        });

        this.updateSpaceUserMessageStreamSubscription = roomConnection.updateSpaceUserMessageStream.subscribe(
            (message) => {
                if (!message.user || !message.updateMask) {
                    throw new Error("updateSpaceUserMessage is missing a user or an updateMask");
                }

                this.spaces.get(message.spaceName)?.updateUserData(message.user, message.updateMask);
            },
        );

        this.removeSpaceUserMessageStreamSubscription = roomConnection.removeSpaceUserMessageStream.subscribe(
            (message) => {
                if (!message.spaceUserId) {
                    throw new Error("removeSpaceUserMessage is missing a spaceUserId");
                }

                this.spaces.get(message.spaceName)?.removeUser(message.spaceUserId);
            },
        );

        this.updateSpaceMetadataMessageStreamSubscription = roomConnection.updateSpaceMetadataMessageStream.subscribe(
            (message) => {
                const isMetadata = z.record(z.string(), z.unknown()).safeParse(JSON.parse(message.metadata));
                if (!isMetadata.success) {
                    console.error("Error while parsing metadata", isMetadata.error);
                    return;
                }
                const metadata: Map<string, unknown> = new Map();
                for (const [key, value] of Object.entries(isMetadata.data)) {
                    metadata.set(key, value);
                }

                if (!message.metadata) {
                    return;
                }

                const space = this.spaces.get(message.spaceName);
                if (!space) {
                    console.error("Space does not exist", message.spaceName);
                    return;
                }

                space.setMetadata(metadata);
            },
        );

        this.spaceStatePatchMessageStreamSubscription = roomConnection.spaceStatePatchMessageStream.subscribe(
            (message) => {
                this.spaces.get(message.spaceName)?.state.applyPatch(message.patch);
            },
        );

        this.proximityPublicMessageEventSubscription = roomConnection.spacePublicMessageEvent.subscribe((message) => {
            const space = this.spaces.get(message.spaceName);
            if (!space) {
                console.warn(
                    `Received a public message for a space that does not exist: "${message.spaceName}". This should not happen unless the space was left a few milliseconds before.`,
                );
                return;
            }
            space.dispatchPublicMessage(message);
        });

        this.proximityPrivateMessageEventSubscription = roomConnection.spacePrivateMessageEvent.subscribe((message) => {
            const space = this.spaces.get(message.spaceName);
            if (!space) {
                console.warn(
                    `Received a private message for a space that does not exist: "${message.spaceName}". This should not happen unless the space was left a few milliseconds before.`,
                );
                return;
            }
            space.dispatchPrivateMessage(message);
        });

        this.spaceDestroyedMessageSubscription = roomConnection.spaceDestroyedMessage.subscribe((message) => {
            console.error(`Space ${message.spaceName} destroyed. Something went wrong server-side.`);
            Sentry.captureException(
                new Error(`Space ${message.spaceName} destroyed. Something went wrong server-side.`),
            );

            const space = this.spaces.get(message.spaceName);
            if (space) {
                space.onDisconnect();
            }
        });
    }

    async joinSpace(
        spaceName: string,
        filterType: FilterType,
        propertiesToSync: string[],
        signal: AbortSignal,
        options?: {
            metadata?: Map<string, unknown>;
            // True if the user is allowed to start/stop recording in the space. Defaults to false.
            canRecord?: boolean;
            /**
             * What the space is, told to the back as well as kept here: a space that
             * declares nothing is nobody's meeting and nobody's broadcast, so the back
             * measures nothing in it.
             */
            spaceKind?: SpaceKind;
        },
    ): Promise<SpaceInterface> {
        const leavingPromise = this.leavingSpacesPromises.get(spaceName);
        if (leavingPromise) {
            await leavingPromise;
        }

        // A join for the same space might already be in flight. Because Space.create() awaits a
        // server round-trip (emitJoinSpace), a naive "exist() check then create" straddles an await:
        // two rapid join attempts (e.g. quickly re-entering a meeting room) can both pass the
        // existence check, then the first registers the space and the second throws
        // SpaceAlreadyExistError (or silently overwrites it, leaking the first Space and leaving
        // remote users visible). We coalesce concurrent joins on the same name by reusing the
        // in-flight creation promise.
        const joiningPromise = this.joiningSpacesPromises.get(spaceName);
        if (joiningPromise) {
            return await joiningPromise;
        }

        if (this.exist(spaceName)) throw new SpaceAlreadyExistError(spaceName);

        // Reserve the space name synchronously (before the first await) so concurrent joins coalesce.
        const creationPromise = (async () => {
            const newSpace = await Space.create(
                spaceName,
                filterType,
                this.roomConnection,
                propertiesToSync,
                signal,
                options,
            );
            this.spaces.set(newSpace.getName(), newSpace);
            if (options?.spaceKind) {
                newSpace.state.setKind(options.spaceKind);
            }
            return newSpace;
        })();
        this.joiningSpacesPromises.set(spaceName, creationPromise);

        try {
            return await creationPromise;
        } finally {
            this.joiningSpacesPromises.delete(spaceName);
        }
    }
    exist(spaceName: string): boolean {
        return this.spaces.has(spaceName);
    }
    async leaveSpace(space: SpaceInterface): Promise<void> {
        const spaceName = space.getName();
        const spaceInRegistry = this.spaces.get(spaceName);
        if (!spaceInRegistry) {
            throw new SpaceDoesNotExistError(spaceName);
        }

        const leavingPromise = this.performLeaveSpace(spaceInRegistry, spaceName);
        this.leavingSpacesPromises.set(spaceName, leavingPromise);

        try {
            await leavingPromise;
        } finally {
            this.leavingSpacesPromises.delete(spaceName);
        }
    }

    private async performLeaveSpace(spaceInRegistry: Space, spaceName: string): Promise<void> {
        await spaceInRegistry.destroy();
        this.spaces.delete(spaceName);
    }
    getAll(): SpaceInterface[] {
        return Array.from(this.spaces.values());
    }
    get(spaceName: string): SpaceInterface {
        const space: SpaceInterface | undefined = this.spaces.get(spaceName);
        if (!space) {
            throw new SpaceDoesNotExistError(spaceName);
        }
        return space;
    }

    async destroy() {
        this.initSpaceUsersMessageStreamSubscription.unsubscribe();
        this.addSpaceUserMessageStreamSubscription.unsubscribe();
        this.updateSpaceUserMessageStreamSubscription.unsubscribe();
        this.removeSpaceUserMessageStreamSubscription.unsubscribe();
        this.updateSpaceMetadataMessageStreamSubscription.unsubscribe();
        this.spaceStatePatchMessageStreamSubscription.unsubscribe();
        this.proximityPublicMessageEventSubscription.unsubscribe();
        this.proximityPrivateMessageEventSubscription.unsubscribe();
        this.spaceDestroyedMessageSubscription.unsubscribe();

        // Wait for any in-flight join to settle so it does not register a space after we have
        // iterated this.spaces below (which would leak it). allSettled because a join may reject.
        await Promise.allSettled(Array.from(this.joiningSpacesPromises.values()));
        this.joiningSpacesPromises.clear();

        await Promise.all(Array.from(this.leavingSpacesPromises.values()));
        this.leavingSpacesPromises.clear();

        await Promise.all(
            Array.from(this.spaces.values()).map(async (space) => {
                try {
                    await space.destroy();
                } finally {
                    this.spaces.delete(space.getName());
                }
                console.warn(`Space "${space.getName()}" was not destroyed properly.`);
            }),
        );
    }
}
